import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Lock, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <p className="text-sm text-[#5c5752]">Lade…</p>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(form.email.trim(), form.password);
      toast.success("Willkommen zurück!");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="admin-login-page">
      <header className="border-b border-warm bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 md:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-brand link-underline"
          >
            <ArrowLeft size={16} />
            Zurück zur Webseite
          </Link>
          <span className="font-display text-2xl font-extrabold tracking-tight text-brand">
            Scoops&Co
          </span>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="w-full max-w-md rounded-3xl border border-warm bg-white p-10 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.15)]"
        >
          <span className="eyebrow">Admin Bereich</span>
          <h1 className="font-display mt-4 text-4xl font-bold tracking-tight text-brand">
            Anmelden
          </h1>
          <p className="mt-3 text-sm text-[#5c5752]">
            Verwaltet hier Bewertungen, Nachrichten und Statistiken.
          </p>

          <div className="mt-8 space-y-5">
            <div>
              <Label
                htmlFor="admin-email"
                className="text-xs uppercase tracking-[0.2em] text-[#5c5752]"
              >
                E-Mail
              </Label>
              <div className="relative mt-2">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5c5752]"
                />
                <Input
                  id="admin-email"
                  type="email"
                  data-testid="admin-input-email"
                  required
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm({ ...form, email: e.target.value })
                  }
                  className="pl-9 border-warm bg-white"
                  placeholder="info@scoopsandco.com"
                />
              </div>
            </div>

            <div>
              <Label
                htmlFor="admin-password"
                className="text-xs uppercase tracking-[0.2em] text-[#5c5752]"
              >
                Passwort
              </Label>
              <div className="relative mt-2">
                <Lock
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5c5752]"
                />
                <Input
                  id="admin-password"
                  type="password"
                  data-testid="admin-input-password"
                  required
                  autoComplete="current-password"
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                  className="pl-9 border-warm bg-white"
                  placeholder="••••••••"
                />
              </div>
            </div>
          </div>

          {error && (
            <p
              data-testid="admin-login-error"
              className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="btn-primary mt-8 w-full disabled:opacity-60"
          >
            {submitting ? "Anmeldung läuft…" : "Anmelden"}
          </button>
        </form>
      </main>
    </div>
  );
}
